/**
 * AI Analysis Popover
 *
 * Shows why an email was categorized a certain way.
 * Displays the AI's reasoning, confidence, and source.
 */

import { useState } from 'react';
import { IconInfoCircle, IconBrain, IconSparkles, IconDatabase, IconUser } from '@tabler/icons-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { EmailAnalysis, getAnalysisLabels } from '@/lib/llm/types';
import { cn } from '@/lib/utils';
import { getStoredSessionId } from '@/lib/cloudflare/auth-client';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const API_BASE = CF_WORKER_URL ? `${CF_WORKER_URL}/api` : '/api';

interface AIAnalysisPopoverProps {
  analysis: EmailAnalysis;
  fromCache?: boolean;
  className?: string;
  gmailMessageId?: string;
  onAnalysisUpdate?: (analysis: EmailAnalysis) => void;
  label?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  do_nothing: 'Inbox',
  delete: 'Delete',
  archive: 'Archive',
  unsubscribe: 'Unsubscribe',
};

interface DebugStep {
  step: string;
  matched?: boolean;
  hit?: boolean;
  rule?: string;
  source?: string;
  confidence?: number;
  model?: string;
  senderKey?: string;
  analysis?: EmailAnalysis;
  labelSanitized?: boolean;
}

interface DebugResult {
  email: {
    subject: string;
    sender: string;
    senderDomain: string;
    senderEmail?: string;
    receivedAt: number;
    gmailCategory?: string;
  };
  steps: DebugStep[];
  final: {
    source: string;
    analysis: EmailAnalysis;
  };
}

export function AIAnalysisPopover({
  analysis,
  fromCache = false,
  className,
  gmailMessageId,
  onAnalysisUpdate,
  label
}: AIAnalysisPopoverProps) {
  const confidencePercent = Math.round((analysis.confidence || 0) * 100);
  const [debugResult, setDebugResult] = useState<DebugResult | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  // Determine the source of the analysis
  const getSource = () => {
    if (analysis.reason?.includes('starred') || analysis.reason?.includes('replied')) {
      return { label: 'Your Gmail activity', icon: IconUser, color: 'text-blue-500' };
    }
    if (analysis.reason?.includes('filter')) {
      return { label: 'Gmail filter', icon: IconUser, color: 'text-blue-500' };
    }
    if (analysis.reason?.includes('never opened')) {
      return { label: 'Behavior signal', icon: IconSparkles, color: 'text-purple-500' };
    }
    if (fromCache) {
      return { label: 'Cached analysis', icon: IconDatabase, color: 'text-gray-500' };
    }
    return { label: 'AI analysis', icon: IconBrain, color: 'text-green-500' };
  };

  const runDebugAnalysis = async () => {
    if (!gmailMessageId) {
      setDebugError('Missing Gmail message ID.');
      return;
    }

    setDebugLoading(true);
    setDebugError(null);
    setDebugResult(null);

    try {
      const sessionId = getStoredSessionId();
      const response = await fetch(`${API_BASE}/emails/debug`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: JSON.stringify({ gmailMessageId }),
        credentials: 'include',
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || 'Failed to debug email');
      }

      const debugData = result.data as DebugResult;
      setDebugResult(debugData);

      // Notify parent of the new analysis so UI can update
      if (debugData.final?.analysis && onAnalysisUpdate) {
        onAnalysisUpdate(debugData.final.analysis);
      }
    } catch (error) {
      setDebugError(error instanceof Error ? error.message : 'Debug failed');
    } finally {
      setDebugLoading(false);
    }
  };

  const source = getSource();
  const SourceIcon = source.icon;
  const hasLabel = Boolean(label);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={cn(
            hasLabel
              ? "inline-flex h-5 items-center gap-1 rounded-full border border-border/40 bg-muted/40 px-2 text-[10px] text-muted-foreground"
              : "inline-flex h-5 w-5 items-center justify-center rounded-full border border-border/40 bg-muted/40 text-muted-foreground/70",
            "hover:text-foreground hover:bg-muted/70 transition-colors",
            className
          )}
          title="Why this categorization?"
        >
          {hasLabel && (
            <span className="uppercase tracking-wide text-[9px] font-semibold">
              {label}
            </span>
          )}
          <IconInfoCircle className={cn("h-3.5 w-3.5", hasLabel && "h-3 w-3")} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center gap-1.5">
            <SourceIcon className={cn("h-3 w-3", source.color)} />
            <span className="text-[11px] font-medium">{source.label}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">
              {confidencePercent}%
            </span>
          </div>

          {/* Category & Label */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">Category</span>
              <span className="text-[11px] font-medium">
                {CATEGORY_LABELS[analysis.category] || analysis.category}
              </span>
            </div>
            {getAnalysisLabels(analysis).length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">Label{getAnalysisLabels(analysis).length > 1 ? 's' : ''}</span>
                <span className="text-[11px] font-medium">{getAnalysisLabels(analysis).join(', ')}</span>
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-0.5">
            <span className="text-[10px] text-muted-foreground">Why</span>
            <p className="text-[11px] leading-relaxed">
              {analysis.reason || 'No reason provided'}
            </p>
          </div>

          {/* Tip */}
          <div className="pt-1.5 border-t border-border/20">
            <p className="text-[10px] text-muted-foreground/70">
              Drag to another column to change.
            </p>
          </div>

          {/* Reanalyze */}
          {gmailMessageId && (
            <div className="pt-1.5 border-t border-border/20 space-y-1.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  runDebugAnalysis();
                }}
                className="text-[10px] text-blue-600 hover:text-blue-700"
                disabled={debugLoading}
              >
                {debugLoading ? 'Analyzing...' : 'Reanalyze'}
              </button>

              {debugError && (
                <p className="text-[10px] text-red-500">{debugError}</p>
              )}

              {debugResult && (
                <div className="text-[10px] text-muted-foreground">
                  Result: <span className="font-medium text-foreground">
                    {CATEGORY_LABELS[debugResult.final.analysis.category] || debugResult.final.analysis.category}
                  </span>{' '}
                  · {getAnalysisLabels(debugResult.final.analysis).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
